import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  GuidanceTourStep,
  GuidanceTourStepCompletionMap,
} from '../types';
import {
  getOnboardingStepIconColor,
  getOnboardingStepTextColor,
  OnboardingStepDisplayState,
  resolveOnboardingStepDisplayState,
} from '../utils/onboardingStepStatusColors';

const SHOW_DELAY_MS = 120;
const HIDE_DELAY_MS = 180;
const POPOVER_Z_INDEX = 10050;

interface StepDependencyHintProps {
  blockedBy: string[];
  steps: GuidanceTourStep[];
  stepIndex: number;
  stepCompleted: GuidanceTourStepCompletionMap;
  onJumpToPrerequisiteStep: (stepId: string) => void;
  children: React.ReactElement;
  /** When false, hover shows the hint but click is left to the child (e.g. accordion toggle). */
  openOnClick?: boolean;
}

const BlockerStatusIcon: React.FC<{
  completed: boolean;
  isCurrent: boolean;
  displayState: OnboardingStepDisplayState;
}> = ({ completed, isCurrent, displayState }) => {
  const theme = useTheme();
  const iconSx = {
    fontSize: 14,
    color: getOnboardingStepIconColor(theme, displayState),
  };

  if (isCurrent) {
    return <PlayCircleOutlineIcon sx={iconSx} />;
  }
  if (completed) {
    return <CheckCircleIcon sx={iconSx} />;
  }
  return <RadioButtonUncheckedIcon sx={iconSx} />;
};

export const StepDependencyHint: React.FC<StepDependencyHintProps> = ({
  blockedBy,
  steps,
  stepIndex,
  stepCompleted,
  onJumpToPrerequisiteStep,
  children,
  openOnClick = true,
}) => {
  const theme = useTheme();
  const anchorRef = useRef<HTMLDivElement>(null);
  const showTimerRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const popoverIdRef = useRef(
    `step-dependency-hint-${Math.random().toString(36).slice(2, 11)}`,
  );
  const popoverId = popoverIdRef.current;
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const blockers = blockedBy
    .map((stepId) => {
      const index = steps.findIndex((candidate) => candidate.id === stepId);
      const step = index >= 0 ? steps[index] : undefined;
      return step ? { stepId, step, index } : null;
    })
    .filter(
      (entry): entry is { stepId: string; step: GuidanceTourStep; index: number } =>
        entry !== null,
    );

  const clearTimers = useCallback(() => {
    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    setPosition({
      top: rect.top + rect.height / 2,
      left: rect.left - 10,
    });
  }, []);

  const openPopover = useCallback(() => {
    clearTimers();
    updatePosition();
    setOpen(true);
  }, [clearTimers, updatePosition]);

  const scheduleOpen = useCallback(() => {
    clearTimers();
    showTimerRef.current = window.setTimeout(() => {
      openPopover();
    }, SHOW_DELAY_MS);
  }, [clearTimers, openPopover]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    hideTimerRef.current = window.setTimeout(() => {
      setOpen(false);
    }, HIDE_DELAY_MS);
  }, [clearTimers]);

  const handlePrerequisiteClick = useCallback(
    (stepId: string, event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      clearTimers();
      setOpen(false);
      onJumpToPrerequisiteStep(stepId);
    },
    [clearTimers, onJumpToPrerequisiteStep],
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    updatePosition();
    const handleReposition = () => updatePosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [open, updatePosition]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  if (blockers.length === 0) {
    return children;
  }

  const interactiveChild = openOnClick
    ? React.cloneElement(children, {
        onClick: (event: React.MouseEvent<HTMLElement>) => {
          event.preventDefault();
          event.stopPropagation();
          openPopover();
        },
      })
    : children;

  const popover =
    open &&
    typeof document !== 'undefined' &&
    createPortal(
      <Paper
        id={popoverId}
        role="tooltip"
        elevation={8}
        data-guidance-tour-dependency-hint="true"
        onMouseEnter={openPopover}
        onMouseLeave={scheduleClose}
        onMouseDown={(event) => event.stopPropagation()}
        sx={{
          position: 'fixed',
          top: position.top,
          left: position.left,
          transform: 'translate(-100%, -50%)',
          zIndex: POPOVER_Z_INDEX,
          width: 280,
          maxWidth: 'min(300px, calc(100vw - 24px))',
          p: 1.25,
          borderRadius: 1.5,
          border: 1,
          borderColor: 'divider',
          pointerEvents: 'auto',
          animation: 'stepDependencyPopoverIn 0.2s ease-out',
          '@keyframes stepDependencyPopoverIn': {
            from: {
              opacity: 0,
              transform: 'translate(calc(-100% + 10px), -50%)',
            },
            to: {
              opacity: 1,
              transform: 'translate(-100%, -50%)',
            },
          },
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mb: 0.75,
            fontSize: '0.6875rem',
            fontWeight: 600,
            lineHeight: 1.35,
          }}
        >
          Come back after these steps are marked complete
        </Typography>
        <List
          dense
          disablePadding
          sx={{
            '& .MuiListItemButton-root': {
              py: 0.125,
              minHeight: 22,
            },
          }}
        >
          {blockers.map(({ stepId, step, index }) => {
            const completed = stepCompleted[step.id] === true;
            const isCurrent = index === stepIndex;
            const displayState = resolveOnboardingStepDisplayState({
              completed,
              isAccessible: true,
            });
            const stepTextColor = getOnboardingStepTextColor(theme, displayState);

            return (
              <ListItemButton
                key={stepId}
                dense
                selected={isCurrent}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => handlePrerequisiteClick(stepId, event)}
                sx={{
                  py: 0.125,
                  px: 0.5,
                  minHeight: 22,
                  borderRadius: 0.75,
                  '&.Mui-selected': {
                    bgcolor: 'action.selected',
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: 'action.selected',
                  },
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 22, mr: 0.375, py: 0 }}>
                  <BlockerStatusIcon
                    completed={completed}
                    isCurrent={isCurrent}
                    displayState={displayState}
                  />
                </ListItemIcon>
                <ListItemText
                  disableTypography
                  sx={{ my: 0, py: 0 }}
                  primary={
                    <Typography
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: '0.75rem',
                        lineHeight: 1.25,
                        fontWeight: isCurrent ? 600 : 400,
                        color: stepTextColor,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {step.title}
                    </Typography>
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </Paper>,
      document.body,
    );

  return (
    <>
      <Box
        ref={anchorRef}
        sx={{ position: 'relative', display: 'block' }}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={openPopover}
        onBlur={scheduleClose}
        aria-describedby={open ? popoverId : undefined}
      >
        {interactiveChild}
      </Box>
      {popover}
    </>
  );
};

export default StepDependencyHint;
